import React, { useState, useEffect } from 'react';
import { adminApi } from '../../../services/api';
import {
  Loader2, Users, Megaphone, TrendingUp, BarChart3,
  ArrowUpRight, Target, Percent,
} from 'lucide-react';

const PERIOD_OPTIONS = [
  { value: 30, label: '30 giorni' },
  { value: 90, label: '90 giorni' },
  { value: 180, label: '6 mesi' },
  { value: 365, label: '1 anno' },
];

export function AcquisitionTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(90);

  useEffect(() => {
    setLoading(true);
    adminApi.get(`/admin/analytics/acquisition?days=${days}`)
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [days]);

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <Loader2 className="animate-spin text-[#D44E20]" size={24} />
    </div>
  );

  if (!data) return <p className="text-center text-[#64748B] py-10">Errore caricamento dati</p>;

  const { by_channel, by_campaign, monthly_trend, kpi } = data;
  const maxCount = Math.max(...by_channel.map(c => c.count), 1);

  return (
    <div className="space-y-6" data-testid="acquisition-analytics">
      {/* Period selector */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-[#1A1A1A]">Acquisizione Clienti</h2>
        <div className="flex gap-1 bg-slate-100 rounded-xl p-0.5">
          {PERIOD_OPTIONS.map(p => (
            <button key={p.value} onClick={() => setDays(p.value)} data-testid={`acq-period-${p.value}`}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${days === p.value ? 'bg-white shadow-sm text-[#1A1A1A]' : 'text-[#64748B] hover:text-[#64748B]'}`}>
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard icon={Users} label="Nuovi clienti (periodo)" value={kpi.period_new} color="#3B82F6" />
        <KpiCard icon={Percent} label="Copertura canale" value={`${kpi.coverage_pct}%`} sub={`${kpi.with_channel}/${kpi.total_customers}`} color="#8B5CF6" />
        <KpiCard icon={Megaphone} label="Da campagne fisiche" value={kpi.with_campaign} color="#F59E0B" />
        <KpiCard icon={Target} label="Totale clienti" value={kpi.total_customers} color="#10B981" />
      </div>

      {/* Channel Distribution */}
      <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm" data-testid="channel-distribution">
        <h3 className="text-sm font-bold text-[#1A1A1A] mb-4">Distribuzione per canale</h3>
        {by_channel.length === 0 ? (
          <div className="text-center py-8">
            <BarChart3 className="mx-auto mb-3 text-[#ccc]" size={40} />
            <p className="text-[#64748B] text-sm">Nessun dato di acquisizione disponibile</p>
            <p className="text-[#aaa] text-xs mt-1">I dati appariranno quando i clienti si registrano specificando il canale</p>
          </div>
        ) : (
          <div className="space-y-3">
            {by_channel.map(ch => (
              <div key={ch.channel} className="group" data-testid={`channel-row-${ch.channel}`}>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: ch.color }} />
                    <span className="text-sm font-medium text-[#1A1A1A]">{ch.label}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-[#64748B]">{ch.pct}%</span>
                    <span className="text-sm font-bold text-[#1A1A1A] tabular-nums w-10 text-right">{ch.count}</span>
                  </div>
                </div>
                <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all duration-500" style={{ width: `${(ch.count / maxCount) * 100}%`, backgroundColor: ch.color }} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Value by Channel */}
      {by_channel.filter(c => c.channel !== 'non_specificato').length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm" data-testid="channel-value">
          <h3 className="text-sm font-bold text-[#1A1A1A] mb-4">Valore per canale</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="text-left py-2 px-2 font-semibold text-[#64748B]">Canale</th>
                  <th className="text-right py-2 px-2 font-semibold text-[#64748B]">Clienti</th>
                  <th className="text-right py-2 px-2 font-semibold text-[#64748B]">Visite medie</th>
                  <th className="text-right py-2 px-2 font-semibold text-[#64748B]">Punti medi</th>
                  <th className="text-right py-2 px-2 font-semibold text-[#64748B]">Spesa media</th>
                </tr>
              </thead>
              <tbody>
                {by_channel.filter(c => c.channel !== 'non_specificato').map(ch => (
                  <tr key={ch.channel} className="border-b border-gray-50 hover:bg-slate-50/50">
                    <td className="py-2.5 px-2">
                      <span className="inline-flex items-center gap-1.5">
                        <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: ch.color }} />
                        <span className="font-medium text-[#1A1A1A]">{ch.label}</span>
                      </span>
                    </td>
                    <td className="py-2.5 px-2 text-right font-bold text-[#1A1A1A] tabular-nums">{ch.count}</td>
                    <td className="py-2.5 px-2 text-right text-[#64748B] tabular-nums">{ch.avg_visits}</td>
                    <td className="py-2.5 px-2 text-right text-[#64748B] tabular-nums">{ch.avg_points}</td>
                    <td className="py-2.5 px-2 text-right font-mono text-[#1A1A1A] tabular-nums">{ch.avg_spend > 0 ? `€${ch.avg_spend}` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Campaign Attribution */}
      {by_campaign.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm" data-testid="campaign-attribution">
          <h3 className="text-sm font-bold text-[#1A1A1A] mb-4 flex items-center gap-2"><Megaphone size={14} /> Campagne fisiche</h3>
          <div className="space-y-3">
            {by_campaign.map(camp => (
              <div key={camp.campaign_id} className="bg-gray-50 rounded-xl p-4" data-testid={`campaign-row-${camp.campaign_id}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold text-[#1A1A1A]">{camp.name}</span>
                  <span className="text-xs font-bold px-2 py-0.5 rounded-lg bg-[#F59E0B]/10 text-[#F59E0B]">{camp.count} clienti</span>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <p className="text-[10px] text-[#64748B]">Visite medie</p>
                    <p className="text-sm font-bold text-[#1A1A1A]">{camp.avg_visits}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-[#64748B]">Punti medi</p>
                    <p className="text-sm font-bold text-[#1A1A1A]">{camp.avg_points}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-[#64748B]">Spesa media</p>
                    <p className="text-sm font-bold text-[#1A1A1A]">{camp.avg_spend > 0 ? `€${camp.avg_spend}` : '—'}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Monthly Trend */}
      {monthly_trend.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm" data-testid="monthly-trend">
          <h3 className="text-sm font-bold text-[#1A1A1A] mb-4 flex items-center gap-2"><TrendingUp size={14} /> Trend mensile acquisizioni</h3>
          <MonthlyChart data={monthly_trend} channels={by_channel} />
        </div>
      )}
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, sub, color }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-4 shadow-sm">
      <div className="flex items-center gap-2 mb-2">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}12` }}>
          <Icon size={16} style={{ color }} />
        </div>
      </div>
      <p className="text-xl font-bold text-[#1A1A1A]">{value}</p>
      <p className="text-[10px] text-[#64748B] mt-0.5">{label}</p>
      {sub && <p className="text-[10px] font-bold mt-0.5" style={{ color }}>{sub}</p>}
    </div>
  );
}

function MonthlyChart({ data, channels }) {
  const maxTotal = Math.max(...data.map(d => d.total), 1);
  const colorMap = {};
  channels.forEach(c => { colorMap[c.channel] = c.color; });

  return (
    <div className="flex items-end gap-2 h-40 overflow-x-auto pb-6" data-testid="monthly-chart">
      {data.map(m => {
        const pct = Math.max(8, (m.total / maxTotal) * 100);
        const segments = Object.entries(m).filter(([k]) => k !== 'month' && k !== 'total');
        return (
          <div key={m.month} className="flex flex-col items-center min-w-[36px] flex-shrink-0 group relative flex-1 max-w-[60px]">
            <div className="w-full rounded-t-lg overflow-hidden flex flex-col-reverse" style={{ height: `${pct}%` }}>
              {segments.map(([ch, count]) => {
                const segPct = (count / m.total) * 100;
                return <div key={ch} style={{ height: `${segPct}%`, backgroundColor: colorMap[ch] || '#9CA3AF', minHeight: '2px' }} />;
              })}
            </div>
            <span className="text-[9px] text-[#aaa] mt-1 whitespace-nowrap">{m.month.slice(5)}</span>
            <div className="absolute bottom-full mb-1 bg-[#111114] text-white text-[9px] px-2 py-1 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
              {m.month}: {m.total} nuovi
            </div>
          </div>
        );
      })}
    </div>
  );
}
